import { compressImage } from "@/components/createPostForm/compressImage";
import { RemoteImage } from "@/components/createPostForm/types";
import { updatePost } from "@/components/createPostForm/updatePost.web";
import { uploadPost } from "@/components/createPostForm/uploadPost.web";
import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import { Button } from "@/components/ui/Button";
import Dropdown from "@/components/ui/Dropdown";
import { IconSymbol } from "@/components/ui/IconSymbol";
import { MaskIcon } from "@/components/ui/MaskIcon";
import { SourceInput } from "@/components/ui/SourceInput";
import VehiclePlateInputV2 from "@/components/ui/VehiclePlateInputV2";
import { COUNTRIES } from "@/constants/countries";
import { NOTIFICATION_TYPES } from "@/constants/notification-types";
import { getPostMaxLength } from "@/constants/postLimits";
import {
  MAX_IMAGE_INPUT_BYTES,
  MAX_IMAGE_INPUT_MB,
  MAX_TOTAL_MEDIA,
  MAX_VIDEO_SIZE_BYTES,
  MAX_VIDEO_SIZE_MB,
  MAX_VIDEOS,
} from "@/constants/uploadLimits";
import { useAuth } from "@/context/AuthContext";
import { usePosts } from "@/context/PostsContext";
import { useToast } from "@/context/ToastContext";
import { useGoogleMapsLoader } from "@/hooks/useGoogleMapsLoader.web";
import { useThemeColor } from "@/hooks/useThemeColor";
import { Post } from "@/types/post";
import { VehiclePlate as VehiclePlateType } from "@/types/vehicle-plate";
import { requestCurrentPositionWithTimeout } from "@/utils/currentPosition";
import { getGroupPictureThumbnail } from "@/utils/getGroupPictureThumbnail";
import { Circle, GoogleMap } from "@react-google-maps/api";
import { Image } from "expo-image";
import * as ImagePicker from "expo-image-picker";
import * as Location from "expo-location";
import { router } from "expo-router";
import { getApp } from "firebase/app";
import { doc, getFirestore, onSnapshot } from "firebase/firestore";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { ActivityIndicator } from "react-native";
import { CustomHeader } from "./CustomHeader";
import CustomModal from "./ui/CustomModal.web";
import { LoadingIndicator } from "./ui/LoadingIndicator";

const MY_LOCATION_BLUE = "#1a73e8";

const firestore = getFirestore(getApp());
const DefaultGroupImage = require("../assets/images/default-group.webp");
const MAP_ID = process.env.EXPO_PUBLIC_MAPS_ID || "DEMO_MAP_ID";

interface CreatePostFormProps {
  groupId?: string | null;
  targetName?: string | null;
  mode?: "create" | "edit";
  initialPost?: Post;
  onSaved?: (updated: Post) => void;
}

export function CreatePostFormV2(
  {
    groupId: groupIdProp,
    targetName,
    mode = "create",
    initialPost,
    onSaved,
  }: CreatePostFormProps = {
    groupId: null,
    targetName: null,
  },
) {
  const isEditMode = mode === "edit" && !!initialPost;
  // In edit mode, groupId is inherited from the post and is immutable.
  const groupId = isEditMode ? initialPost?.groupId ?? null : groupIdProp;
  const [mapModalVisible, setMapModalVisible] = useState(false);
  const { user, userData } = useAuth();
  const { addPost } = usePosts();
  const { t } = useTranslation();
  const [description, setDescription] = useState("");
  const [vehiclePlates, setVehiclePlates] = useState<VehiclePlateType[]>([]);
  const [eventType, setEventType] = useState("NOTIFICATION");
  const stripesColor = useThemeColor({}, "stripes");
  const vehiclePlateInputRef = useRef<{
    submit: () => boolean;
    reset?: () => void;
  } | null>(null);
  const descriptionRef = useRef<HTMLTextAreaElement>(null);
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { addToast, removeToast } = useToast();

  const [customCoords, setCustomCoords] = useState<{
    latitude: number;
    longitude: number;
  } | null>(null);
  const [locationString, setLocationString] = useState("");
  const [eventDate, setEventDate] = useState(new Date());
  const [images, setImages] = useState<
    (ImagePicker.ImagePickerAsset | RemoteImage)[]
  >([]);
  const [initialImages, setInitialImages] = useState<string[]>([]);

  const [plateFollowersOnly, setPlateFollowersOnly] = useState(false);
  const [audiencePulseKey, setAudiencePulseKey] = useState(0);
  const [audienceHighlighted, setAudienceHighlighted] = useState(false);
  const [sourceUrl, setSourceUrl] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const [isFetchingLocation, setIsFetchingLocation] = useState(false);
  const [showDateRow, setShowDateRow] = useState(false);
  const [eventDateModified, setEventDateModified] = useState(false);

  const isGroupPost = !!groupId;

  const [groupFixedLocation, setGroupFixedLocation] = useState<{
    latitude: number;
    longitude: number;
    city?: string;
    country?: string;
  } | null>(null);
  const [groupAllowsOverride, setGroupAllowsOverride] = useState(false);
  const hasSeededLocationRef = useRef(false);
  const locationLocked = !!groupFixedLocation && !groupAllowsOverride;

  const [alertModal, setAlertModal] = useState<{
    visible: boolean;
    title: string;
    message: string;
  }>({ visible: false, title: "", message: "" });

  const [confirmModal, setConfirmModal] = useState<{
    visible: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({ visible: false, title: "", message: "", onConfirm: () => {} });

  const showAlert = (title: string, message: string) => {
    setAlertModal({ visible: true, title, message });
  };

  const showConfirm = (
    title: string,
    message: string,
    onConfirm: () => void,
  ) => {
    setConfirmModal({ visible: true, title, message, onConfirm });
  };

  const color = useThemeColor({}, "text");
  const backgroundColor = useThemeColor({}, "background");
  const tintColor = useThemeColor({}, "tint");
  const iconColor = useThemeColor({}, "icon");
  const maxLength = getPostMaxLength(userData?.isVerified);
  const atLimit = description.length >= maxLength;

  const [isProcessingMedia, setIsProcessingMedia] = useState(false);

  const { isLoaded, loadError } = useGoogleMapsLoader();

  const placeAutocompleteContainerRef = useRef<HTMLDivElement | null>(null);
  const placeAutocompleteRef = useRef<any>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const advancedMarkerRef = useRef<any>(null);
  const mapFeatureWarningShownRef = useRef(false);
  const [mapInstanceReady, setMapInstanceReady] = useState(false);
  const [myPos, setMyPos] = useState<{ lat: number; lng: number } | null>(null);

  useEffect(() => {
    if (!mapModalVisible) {
      mapFeatureWarningShownRef.current = false;
      setMapInstanceReady(false);
    }
  }, [mapModalVisible]);

  const refreshMyPos = useCallback(async () => {
    const coords = await requestCurrentPositionWithTimeout();
    if (!coords) {
      setMyPos(null);
      return null;
    }
    const next = { lat: coords.latitude, lng: coords.longitude };
    setMyPos(next);
    return next;
  }, []);

  useEffect(() => {
    if (!mapModalVisible) return;
    void refreshMyPos();
  }, [mapModalVisible, refreshMyPos]);

  const handleRecenter = useCallback(async () => {
    const next = (await refreshMyPos()) ?? myPos;
    if (next && mapRef.current) {
      mapRef.current.panTo(next);
    }
  }, [refreshMyPos, myPos]);

  const prevPlatesEmptyRef = useRef(true);
  useEffect(() => {
    const wasEmpty = prevPlatesEmptyRef.current;
    const isEmpty = vehiclePlates.length === 0;
    if (!wasEmpty && isEmpty) {
      setPlateFollowersOnly(false);
      setAudiencePulseKey((k) => k + 1);
    }
    prevPlatesEmptyRef.current = isEmpty;
  }, [vehiclePlates.length]);

  useEffect(() => {
    if (audiencePulseKey === 0) return;
    setAudienceHighlighted(true);
    const timer = setTimeout(() => setAudienceHighlighted(false), 1200);
    return () => clearTimeout(timer);
  }, [audiencePulseKey]);

  const warnMapFeaturesUnavailable = useCallback(() => {
    if (mapFeatureWarningShownRef.current) return;
    mapFeatureWarningShownRef.current = true;
    addToast(t("MAP_FEATURES_UNAVAILABLE"), "error");
  }, [addToast, t]);

  useEffect(() => {
    if (!isLoaded || !mapModalVisible || !mapInstanceReady) return;
    const map = mapRef.current;
    if (!map) return;

    try {
      const markerNs = (window as any).google?.maps?.marker;
      if (!markerNs?.AdvancedMarkerElement) {
        throw new Error("AdvancedMarkerElement unavailable");
      }

      if (!customCoords) {
        if (advancedMarkerRef.current) {
          advancedMarkerRef.current.map = null;
          advancedMarkerRef.current = null;
        }
        return;
      }

      const position = {
        lat: customCoords.latitude,
        lng: customCoords.longitude,
      };

      if (advancedMarkerRef.current) {
        advancedMarkerRef.current.position = position;
        advancedMarkerRef.current.map = map;
      } else {
        advancedMarkerRef.current = new markerNs.AdvancedMarkerElement({
          map,
          position,
        });
      }
    } catch (e) {
      console.warn("Failed to render map marker", e);
      warnMapFeaturesUnavailable();
    }

    return () => {
      if (advancedMarkerRef.current) {
        advancedMarkerRef.current.map = null;
        advancedMarkerRef.current = null;
      }
    };
  }, [
    isLoaded,
    mapModalVisible,
    mapInstanceReady,
    customCoords,
    warnMapFeaturesUnavailable,
  ]);

  useEffect(() => {
    if (!isLoaded || !mapModalVisible) return;
    if (locationLocked) return;
    const container = placeAutocompleteContainerRef.current;
    if (!container) return;

    let element: any = null;
    let handleSelect: ((event: any) => Promise<void>) | null = null;

    try {
      const placesNs = (window as any).google?.maps?.places;
      if (!placesNs?.PlaceAutocompleteElement) {
        throw new Error("PlaceAutocompleteElement unavailable");
      }
      element = new placesNs.PlaceAutocompleteElement();
      placeAutocompleteRef.current = element;
      element.style.width = "100%";
      if ("placeholder" in element) {
        element.placeholder = t("SEARCH_PLACE_PLACEHOLDER");
      }
      container.appendChild(element);

      handleSelect = async (event: any) => {
        try {
          const place = event.placePrediction?.toPlace?.();
          if (!place) return;
          await place.fetchFields({ fields: ["location"] });
          const loc = place.location;
          if (loc) {
            setCustomCoords({
              latitude: loc.lat(),
              longitude: loc.lng(),
            });
          }
        } catch (e) {
          console.warn("Place selection failed", e);
          addToast(t("PLACE_SELECTION_ERROR"), "error");
        }
      };

      element.addEventListener("gmp-select", handleSelect);
    } catch (e) {
      console.warn("Failed to init place search", e);
      warnMapFeaturesUnavailable();
    }

    return () => {
      if (element && handleSelect) {
        element.removeEventListener("gmp-select", handleSelect);
      }
      if (element?.parentNode === container) {
        container.removeChild(element);
      }
      placeAutocompleteRef.current = null;
    };
  }, [
    isLoaded,
    mapModalVisible,
    locationLocked,
    t,
    addToast,
    warnMapFeaturesUnavailable,
  ]);

  useEffect(() => {
    const element = placeAutocompleteRef.current;
    if (!element || !customCoords) return;
    element.locationBias = {
      center: {
        lat: customCoords.latitude,
        lng: customCoords.longitude,
      },
      radius: 50000,
    };
  }, [customCoords, mapModalVisible]);

  useEffect(() => {
    if (customCoords && isLoaded) {
      const reverseGeocode = async () => {
        try {
          if (
            typeof window !== "undefined" &&
            window.google &&
            window.google.maps
          ) {
            const geocoder = new window.google.maps.Geocoder();
            const response = await geocoder.geocode({
              location: {
                lat: customCoords.latitude,
                lng: customCoords.longitude,
              },
            });

            if (response.results && response.results.length > 0) {
              const result = response.results[0];
              let city = "";
              let country = "";
              for (const component of result.address_components) {
                if (component.types.includes("locality")) {
                  city = component.long_name;
                }
                if (
                  !city &&
                  component.types.includes("administrative_area_level_1")
                ) {
                  city = component.long_name;
                }
                if (component.types.includes("country")) {
                  country = component.short_name;
                }
              }
              setLocationString(
                city && country ? `${city}, ${country}` : city || country || "",
              );
            } else {
              setLocationString(
                `Lat: ${customCoords.latitude.toFixed(
                  4,
                )}, Lng: ${customCoords.longitude.toFixed(4)}`,
              );
            }
          }
        } catch (e) {
          console.warn("Reverse geocoding failed", e);
          setLocationString(
            `Lat: ${customCoords.latitude.toFixed(
              4,
            )}, Lng: ${customCoords.longitude.toFixed(4)}`,
          );
        }
      };
      reverseGeocode();
    } else {
      setLocationString("");
    }
  }, [customCoords, isLoaded]);

  useEffect(() => {
    if (!groupId) {
      setGroupFixedLocation(null);
      setGroupAllowsOverride(false);
      hasSeededLocationRef.current = false;
      return;
    }
    const unsubscribe = onSnapshot(
      doc(firestore, "groups", groupId),
      (snap) => {
        if (!snap.exists()) {
          setGroupFixedLocation(null);
          setGroupAllowsOverride(false);
          return;
        }
        const data = snap.data() || {};
        const gp = data.location?.geoPoint;
        if (gp) {
          setGroupFixedLocation({
            latitude: gp.latitude,
            longitude: gp.longitude,
            city: data.location?.city,
            country: data.location?.country,
          });
        } else {
          setGroupFixedLocation(null);
        }
        setGroupAllowsOverride(!!data.allowMemberLocationOverride);
      },
    );
    return () => unsubscribe();
  }, [groupId]);

  useEffect(() => {
    if (!groupFixedLocation) return;
    if (locationLocked) {
      setCustomCoords({
        latitude: groupFixedLocation.latitude,
        longitude: groupFixedLocation.longitude,
      });
      return;
    }
    if (!hasSeededLocationRef.current) {
      hasSeededLocationRef.current = true;
      setCustomCoords({
        latitude: groupFixedLocation.latitude,
        longitude: groupFixedLocation.longitude,
      });
    }
  }, [groupFixedLocation, locationLocked]);

  const processPickerResult = async (result: ImagePicker.ImagePickerResult) => {
    if (result.canceled) {
      return;
    }

    const warnings = new Set<string>();
    let videoCount = images.filter((a) => a.type === "video").length;
    let projectedCount = images.length;
    const toIngest: ImagePicker.ImagePickerAsset[] = [];

    for (const asset of result.assets) {
      if (projectedCount >= MAX_TOTAL_MEDIA) {
        warnings.add(
          t("MEDIA_LIMIT_REACHED_MESSAGE", { count: MAX_TOTAL_MEDIA }),
        );
        break;
      }

      if (asset.type === "video") {
        if (videoCount >= MAX_VIDEOS) {
          warnings.add(t("VIDEO_LIMIT_REACHED_MESSAGE", { count: MAX_VIDEOS }));
          continue;
        }
        if (asset.fileSize && asset.fileSize > MAX_VIDEO_SIZE_BYTES) {
          const sizeMb = (asset.fileSize / 1024 / 1024).toFixed(1);
          warnings.add(
            t("VIDEO_TOO_LARGE_MESSAGE", {
              size: sizeMb,
              count: MAX_VIDEO_SIZE_MB,
            }),
          );
          continue;
        }
        videoCount++;
      } else {
        if (asset.fileSize && asset.fileSize > MAX_IMAGE_INPUT_BYTES) {
          warnings.add(
            t("IMAGE_INPUT_SIZE_LIMIT_MESSAGE", {
              count: MAX_IMAGE_INPUT_MB,
            }),
          );
          continue;
        }
      }
      projectedCount++;
      toIngest.push(asset);
    }

    if (toIngest.length > 0) {
      const optimizingToastId = addToast([t("OPTIMIZING_IMAGES")], "loading");
      setIsProcessingMedia(true);
      try {
        const processed = await Promise.all(
          toIngest.map(async (asset) => {
            if (asset.type === "video") return asset;
            try {
              return await compressImage(asset);
            } catch (err) {
              console.warn("Image compression failed, uploading original", err);
              return asset;
            }
          }),
        );
        setImages((prev) => [...prev, ...processed]);
      } finally {
        setIsProcessingMedia(false);
        removeToast(optimizingToastId);
      }
    }

    if (warnings.size > 0) {
      showAlert(t("FILES_NOT_ADDED_WARNING"), Array.from(warnings).join("\n"));
    }
  };

  const handleMediaPick = async () => {
    if (isProcessingMedia) return;
    if (images.length >= MAX_TOTAL_MEDIA) {
      showAlert(
        t("MEDIA_LIMIT_REACHED"),
        t("MEDIA_LIMIT_REACHED_MESSAGE", { count: MAX_TOTAL_MEDIA }),
      );
      return;
    }

    const resultLibrary = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.All,
      allowsMultipleSelection: true,
      quality: 1,
    });
    processPickerResult(resultLibrary);
  };

  const availableCountries = useMemo(() => {
    if (!userData?.country) return COUNTRIES;
    const userCountry = COUNTRIES.find((c) => c.value === userData.country);
    if (!userCountry) return COUNTRIES;
    const nearbyCountries = userCountry.nearby.map((countryCode) =>
      COUNTRIES.find((c) => c.value === countryCode),
    );
    return [
      userCountry,
      ...(nearbyCountries.filter((c) => c !== undefined) as any),
    ];
  }, [userData?.country]);

  // Seed form state from initialPost on first render in edit mode.
  // Done in a layout effect-equivalent (one-shot ref guard) so the user's
  // subsequent edits aren't clobbered by this seed. The post itself may
  // arrive asynchronously, so we also re-check once initialPost resolves.
  const hasSeededEditRef = useRef(false);
  useEffect(() => {
    if (!isEditMode || !initialPost || hasSeededEditRef.current) return;
    hasSeededEditRef.current = true;

    setDescription(initialPost.description || "");
    setEventType(initialPost.eventType || "NOTIFICATION");
    setPlateFollowersOnly(!!initialPost.plateFollowersOnly);
    setSourceUrl(initialPost.sourceUrl || "");
    setAnonymous(initialPost.anonymous === true);
    // initialPost.vehiclePlates is already VehiclePlate[] — mapDocToPost
    // converts the stored strings to objects on read. Re-mapping would
    // call .split() on an object and crash.
    setVehiclePlates(initialPost.vehiclePlates ?? []);
    if (initialPost.eventDate && (initialPost.eventDate as any).toDate) {
      setEventDate((initialPost.eventDate as any).toDate());
      setEventDateModified(true);
    }
    const geo = initialPost.location?.geoPoint;
    if (geo && (geo as any).latitude != null) {
      setCustomCoords({
        latitude: (geo as any).latitude,
        longitude: (geo as any).longitude,
      });
      const parts = [
        initialPost.location?.city,
        initialPost.location?.country,
      ].filter(Boolean);
      if (parts.length) setLocationString(parts.join(", "));
    }
    const remote = (initialPost.images || []).map((uri): RemoteImage => ({
      uri,
      remote: true,
      type: /\.(mp4|mov|webm|m4v)(\?|$)/i.test(uri) ? "video" : "image",
    }));
    setImages(remote);
    setInitialImages(initialPost.images || []);
  }, [isEditMode, initialPost]);

  const handlePost = async () => {
    if (!user) {
      showAlert(t("ERROR"), t("YOU_MUST_BE_LOGGED_IN_TO_POST"));
      return;
    }

    const toastId = addToast(
      [t("UPLOADING_POST"), t("UPLOADING_POST_DONT_CLOSE")],
      "loading",
    );

    if (groupId) {
      router.replace({
        pathname: "/(tabs)/groups/[id]",
        params: { id: groupId },
      });
    } else {
      router.replace("/(tabs)");
    }

    try {
      await uploadPost({
        images: images as ImagePicker.ImagePickerAsset[],
        user: { uid: user.uid },
        customCoords,
        userData,
        groupId,
        eventDate,
        description,
        vehiclePlates,
        eventType,
        plateFollowersOnly,
        sourceUrl: sourceUrl.trim() || undefined,
        anonymous: !isGroupPost && anonymous,
        addPost,
      });

      addToast(t("POST_UPLOADED_SUCCESSFULLY"), "success");
      setDescription("");
      setVehiclePlates([]);
      setEventType("NOTIFICATION");
      setEventDate(new Date());
      setCustomCoords(null);
      setImages([]);
      setPlateFollowersOnly(false);
      setAnonymous(false);
      setSourceUrl("");
      setShowDateRow(false);
      setEventDateModified(false);
      vehiclePlateInputRef.current?.reset?.();
    } catch (error) {
      console.error("Error posting document: ", error);
      addToast(t("FAILED_TO_SUBMIT_POST"), "error");
    } finally {
      removeToast(toastId);
    }
  };

  const handleUpdate = async () => {
    if (!user || !initialPost) return;

    const toastId = addToast([t("SAVING_POST")], "loading");
    try {
      await updatePost({
        postId: initialPost.id,
        initialImages,
        images,
        user: { uid: user.uid },
        userData,
        customCoords,
        eventDate,
        description,
        vehiclePlates,
        eventType,
        plateFollowersOnly,
        sourceUrl: sourceUrl.trim() || undefined,
        onSaved,
      });
      addToast(t("POST_SAVED_SUCCESSFULLY"), "success");
    } catch (error) {
      console.error("Error updating post: ", error);
      addToast(t("FAILED_TO_SAVE_POST"), "error");
    } finally {
      removeToast(toastId);
    }
  };

  const confirmAndPost = () => {
    if (isEditMode) {
      showConfirm(t("EDIT_POST"), t("CONFIRM_SAVE_EDIT"), handleUpdate);
      return;
    }
    const base = t("ARE_YOU_SURE_YOU_WANT_TO_SUBMIT_THIS_POST");
    const message =
      !isGroupPost && anonymous
        ? `${base}\n\n${t("POST_ANONYMOUSLY_HELP")}`
        : base;
    showConfirm(t("CONFIRM_POST"), message, handlePost);
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const dateStr = e.target.value;
    if (!dateStr) return;
    const [year, month, day] = dateStr.split("-").map(Number);
    const newDate = new Date(eventDate);
    newDate.setFullYear(year);
    newDate.setMonth(month - 1);
    newDate.setDate(day);
    setEventDate(newDate);
    setEventDateModified(true);
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const timeStr = e.target.value;
    if (!timeStr) return;
    const [hours, minutes] = timeStr.split(":").map(Number);
    const newDate = new Date(eventDate);
    newDate.setHours(hours);
    newDate.setMinutes(minutes);
    setEventDate(newDate);
    setEventDateModified(true);
  };

  return (
    <ThemedView style={{ flex: 1, backgroundColor: stripesColor }}>
      <CustomHeader
        title={isEditMode ? t("EDIT_POST") : t("POST")}
        type="subtitle"
        variant="tab"
        showBackButton
      />
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          overflowY: "auto",
        }}
      >
        <div
          style={{
            backgroundColor,
            padding: 20,
            paddingTop: 10,
            paddingBottom: 40,
            width: "100%",
            maxWidth: 680,
            flexGrow: 1,
            flexShrink: 0,
            boxSizing: "border-box",
          }}
        >
          {isGroupPost ? (
            <div style={{ marginBottom: 16 }}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  marginBottom: 12,
                }}
              >
                <Image
                  source={{ uri: getGroupPictureThumbnail(groupId) }}
                  placeholder={DefaultGroupImage}
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: 10,
                    marginRight: 6,
                  }}
                  cachePolicy="disk"
                  accessibilityLabel={t("GROUP_AVATAR_ALT")}
                  transition={300}
                />
                <ThemedText
                  numberOfLines={1}
                  ellipsizeMode="tail"
                  style={{ color, fontWeight: "500" }}
                >
                  {targetName || t("GROUPS")}
                </ThemedText>
              </div>
            </div>
          ) : (
            <button
              type="button"
              role="switch"
              aria-checked={anonymous}
              aria-disabled={isEditMode}
              data-testid="anonymous-toggle"
              disabled={isEditMode}
              onClick={() => {
                if (isEditMode) return;
                setAnonymous((v) => !v);
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                background: "transparent",
                border: "none",
                padding: "8px 2px",
                cursor: isEditMode ? "not-allowed" : "pointer",
                opacity: isEditMode ? 0.5 : 1,
                color,
                width: "100%",
                textAlign: "left",
                marginBottom: 4,
              }}
            >
              <MaskIcon size={18} color={color} style={{ flexShrink: 0 }} />
              <ThemedText
                style={{ fontWeight: "500", fontSize: 14, flex: 1 }}
              >
                {t("POST_ANONYMOUSLY")}
              </ThemedText>
              <span
                style={{
                  flexShrink: 0,
                  width: 36,
                  height: 20,
                  borderRadius: 10,
                  background: anonymous ? tintColor : "#d1d5db",
                  position: "relative",
                  display: "inline-block",
                  transition: "background 0.15s",
                }}
              >
                <span
                  style={{
                    position: "absolute",
                    top: 2,
                    left: anonymous ? 18 : 2,
                    width: 16,
                    height: 16,
                    borderRadius: 8,
                    background: "#fff",
                    transition: "left 0.15s",
                  }}
                />
              </span>
            </button>
          )}

          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
              marginBottom: 16,
            }}
          >
            {!isGroupPost && (
              <div
                className={audienceHighlighted ? "audience-pulse" : ""}
                style={{ minWidth: 0 }}
              >
                <Dropdown
                  testID="audience-dropdown"
                  value={plateFollowersOnly ? "PLATE_FOLLOWERS" : "PUBLIC"}
                  onChange={(v) =>
                    setPlateFollowersOnly(v === "PLATE_FOLLOWERS")
                  }
                  options={[
                    { value: "PUBLIC", label: t("PUBLIC"), icon: "🌐" },
                    ...(vehiclePlates.length > 0
                      ? [
                          {
                            value: "PLATE_FOLLOWERS",
                            label: t("PLATE_FOLLOWERS_ONLY"),
                            icon: "🔒",
                          },
                        ]
                      : []),
                  ]}
                  triggerClassName="max-w-[220px]"
                />
              </div>
            )}
            <Dropdown
              testID="event-type-dropdown"
              value={eventType}
              onChange={setEventType}
              options={Object.entries(NOTIFICATION_TYPES).map(
                ([value, type]) => ({
                  value,
                  label: t(type.title),
                  icon: type.icon,
                }),
              )}
              iconOnlyTrigger
              ariaLabel={
                NOTIFICATION_TYPES[
                  eventType as keyof typeof NOTIFICATION_TYPES
                ]
                  ? t(
                      NOTIFICATION_TYPES[
                        eventType as keyof typeof NOTIFICATION_TYPES
                      ].title,
                    )
                  : undefined
              }
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "flex-start",
              }}
            >
              <ThemedText
                style={{
                  color: iconColor,
                  fontWeight: "600",
                  marginRight: 6,
                  flexShrink: 0,
                  paddingTop: 8,
                }}
              >
                {t("GROUP_POST_RECIPIENT_LABEL")}
              </ThemedText>
              <div style={{ flex: 1, minWidth: 0 }}>
                <VehiclePlateInputV2
                  ref={vehiclePlateInputRef}
                  plates={vehiclePlates}
                  onChange={setVehiclePlates}
                  limit={3}
                  countries={availableCountries}
                  defaultCountry={userData?.country || undefined}
                  onFirstPlateAdded={() => descriptionRef.current?.focus()}
                />
              </div>
            </div>
          </div>

          <div
            style={{
              padding: 8,
              marginBottom: 16,
            }}
          >
            <textarea
              ref={descriptionRef}
              data-testid="post-description-input"
              placeholder={t("DESCRIPTION_PLACEHOLDER")}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={maxLength}
              style={{
                height: 110,
                width: "100%",
                color,
                backgroundColor,
                border: "none",
                outline: "none",
                caretColor: tintColor,
                resize: "none",
                fontFamily: "inherit",
                fontSize: 16,
              }}
            />
            <div
              data-testid="post-char-counter"
              style={{
                textAlign: "right",
                fontSize: 12,
                color: atLimit ? "#e11d48" : iconColor,
                marginTop: 4,
              }}
            >
              {description.length} / {maxLength}
            </div>
            {images.length > 0 && (
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  marginTop: 8,
                  marginBottom: 8,
                  overflowX: "auto",
                  whiteSpace: "nowrap",
                  maxWidth: "100%",
                  flexWrap: "nowrap",
                  boxSizing: "border-box",
                }}
              >
                {images.map((asset, idx) => (
                  <div key={idx} style={{ position: "relative" }}>
                    {asset.type === "video" ? (
                      <video
                        src={asset.uri}
                        controls
                        style={{ width: 80, height: 80, borderRadius: 8 }}
                      />
                    ) : (
                      <img
                        src={asset.uri}
                        alt=""
                        style={{ width: 80, height: 80, borderRadius: 8 }}
                      />
                    )}
                    <button
                      style={{
                        position: "absolute",
                        top: 2,
                        right: 2,
                        background: "rgba(0,0,0,0.6)",
                        borderRadius: 10,
                        width: 20,
                        height: 20,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: 0,
                        border: "none",
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        setImages((prev) => prev.filter((_, i) => i !== idx));
                      }}
                    >
                      <span
                        style={{
                          display: "block",
                          color: "#fff",
                          fontWeight: "bold",
                          fontSize: 12,
                          lineHeight: "14px",
                        }}
                      >
                        ×
                      </span>
                    </button>
                  </div>
                ))}
              </div>
            )}
            {locationString ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: 8,
                }}
              >
                <button
                  onClick={() => setMapModalVisible(true)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    background: "none",
                    border: `1px solid ${iconColor}`,
                    borderRadius: 8,
                    padding: "4px 10px",
                    cursor: "pointer",
                  }}
                >
                  <IconSymbol name="location" size={14} color="#b50000" />
                  <ThemedText style={{ marginLeft: 6 }}>
                    {locationString}
                  </ThemedText>
                </button>
                {locationLocked && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginLeft: 8,
                    }}
                  >
                    <ThemedText style={{ color: iconColor, fontSize: 13 }}>
                      {t("LOCATION_FIXED")}
                    </ThemedText>
                    <IconSymbol
                      name="padlock"
                      size={13}
                      color={iconColor}
                      style={{ marginLeft: 4 }}
                    />
                  </div>
                )}
                {!locationLocked && (
                  <button
                    onClick={() => {
                      setCustomCoords(null);
                    }}
                    style={{
                      marginLeft: 8,
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                    }}
                  >
                    <IconSymbol
                      name="xmark.circle.fill"
                      size={15}
                      color="gray"
                    />
                  </button>
                )}
              </div>
            ) : null}
            <div
              style={{
                borderTop: `1px solid ${stripesColor}`,
                paddingTop: 5,
                display: "flex",
                alignItems: "center",
              }}
            >
              <button
                data-testid="media-pick-button"
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                }}
                onClick={handleMediaPick}
              >
                <IconSymbol
                  name="attach.file"
                  color={tintColor}
                  style={{ marginLeft: 8 }}
                />
              </button>
              <button
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                }}
                disabled={isFetchingLocation}
                onClick={async () => {
                  if (isFetchingLocation) return;
                  if (locationLocked) {
                    setMapModalVisible(true);
                    return;
                  }
                  setIsFetchingLocation(true);

                  try {
                    if (customCoords) {
                      setMapModalVisible(true);
                      return;
                    }

                    const { status } =
                      await Location.requestForegroundPermissionsAsync();

                    let coords = null;
                    if (status === "granted") {
                      coords = await requestCurrentPositionWithTimeout();
                      if (!coords) {
                        addToast(t("LOCATION_UNAVAILABLE"), "info");
                      }
                    }

                    if (!coords) {
                      const userCountry = COUNTRIES.find(
                        (c) => c.value === userData?.country,
                      );
                      if (userCountry?.coords) {
                        coords = userCountry.coords;
                      }
                    }

                    if (coords) {
                      setCustomCoords(coords);
                    }
                    setMapModalVisible(true);
                  } catch (error) {
                    console.warn("Error handling location", error);
                    setMapModalVisible(true); // Open map even on error
                  } finally {
                    setIsFetchingLocation(false);
                  }
                }}
              >
                {isFetchingLocation ? (
                  <ActivityIndicator
                    color={tintColor}
                    style={{ marginLeft: 8 }}
                  />
                ) : (
                  <IconSymbol
                    name="location"
                    color={
                      locationLocked
                        ? iconColor
                        : customCoords
                          ? "#b50000ff"
                          : tintColor
                    }
                    style={{ marginLeft: 8 }}
                  />
                )}
              </button>
              <button
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                }}
                onClick={() => setShowDateRow((v) => !v)}
                aria-label={t("GROUP_POST_EVENT_DATE")}
              >
                <IconSymbol
                  name="calendar"
                  color={eventDateModified ? "#b50000ff" : tintColor}
                  style={{ marginLeft: 8 }}
                />
              </button>
              <SourceInput sourceUrl={sourceUrl} setSourceUrl={setSourceUrl} />
            </div>
            {showDateRow && (
              <div
                style={{
                  marginTop: 12,
                  marginBottom: 8,
                  display: "flex",
                  gap: 16,
                }}
              >
                <input
                  type="date"
                  value={`${eventDate.getFullYear()}-${String(
                    eventDate.getMonth() + 1,
                  ).padStart(2, "0")}-${String(eventDate.getDate()).padStart(
                    2,
                    "0",
                  )}`}
                  max={`${new Date().getFullYear()}-${String(
                    new Date().getMonth() + 1,
                  ).padStart(2, "0")}-${String(new Date().getDate()).padStart(
                    2,
                    "0",
                  )}`}
                  onChange={handleDateChange}
                  style={{
                    flex: 1,
                    color,
                    backgroundColor,
                    border: `1px solid ${iconColor}`,
                    borderRadius: 8,
                    padding: 12,
                  }}
                />
                <input
                  type="time"
                  value={`${String(eventDate.getHours()).padStart(
                    2,
                    "0",
                  )}:${String(eventDate.getMinutes()).padStart(2, "0")}`}
                  onChange={handleTimeChange}
                  style={{
                    flex: 1,
                    color,
                    backgroundColor,
                    border: `1px solid ${iconColor}`,
                    borderRadius: 8,
                    padding: 12,
                  }}
                />
              </div>
            )}
          </div>

          {mapModalVisible && (
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor,
              }}
            >
              {loadError ? (
                <ThemedText style={{ padding: 20, textAlign: "center" }}>
                  {t("MAP_LOAD_ERROR")}
                </ThemedText>
              ) : isLoaded ? (
                <>
                  {locationLocked && (
                    <div
                      style={{
                        position: "absolute",
                        top: 20,
                        left: "50%",
                        transform: "translateX(-50%)",
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        padding: "6px 12px",
                        backgroundColor: "rgba(255,255,255,0.95)",
                        borderRadius: 16,
                        zIndex: 2,
                      }}
                    >
                      <IconSymbol name="padlock" size={14} color={iconColor} />
                      <ThemedText style={{ color: iconColor, fontSize: 13 }}>
                        {t("LOCATION_FIXED")}
                      </ThemedText>
                    </div>
                  )}
                  {!locationLocked && (
                    <div
                      ref={placeAutocompleteContainerRef}
                      style={{
                        position: "absolute",
                        top: 20,
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: "calc(100% - 40px)",
                        maxWidth: 480,
                        zIndex: 1,
                      }}
                    />
                  )}
                  <GoogleMap
                    mapContainerStyle={{ width: "100%", height: "100%" }}
                    options={{
                      mapId: MAP_ID,
                      mapTypeControl: false,
                      streetViewControl: false,
                      fullscreenControl: false,
                      zoomControl: false,
                      cameraControl: false,
                    }}
                    onLoad={(map) => {
                      mapRef.current = map;
                      setMapInstanceReady(true);
                    }}
                    onUnmount={() => {
                      mapRef.current = null;
                    }}
                    center={{
                      lat: customCoords?.latitude ?? 0,
                      lng: customCoords?.longitude ?? 0,
                    }}
                    zoom={15}
                    onDblClick={
                      locationLocked
                        ? undefined
                        : (e) => {
                            if (e.latLng) {
                              setCustomCoords({
                                latitude: e.latLng.lat(),
                                longitude: e.latLng.lng(),
                              });
                            }
                          }
                    }
                    onMouseDown={
                      locationLocked
                        ? undefined
                        : (e) => {
                            if (!e.latLng) return;
                            const lat = e.latLng.lat();
                            const lng = e.latLng.lng();
                            if (longPressTimerRef.current) {
                              clearTimeout(longPressTimerRef.current);
                            }
                            longPressTimerRef.current = setTimeout(() => {
                              setCustomCoords({
                                latitude: lat,
                                longitude: lng,
                              });
                              longPressTimerRef.current = null;
                            }, 500);
                          }
                    }
                    onMouseUp={
                      locationLocked
                        ? undefined
                        : () => {
                            if (longPressTimerRef.current) {
                              clearTimeout(longPressTimerRef.current);
                              longPressTimerRef.current = null;
                            }
                          }
                    }
                    onDragStart={
                      locationLocked
                        ? undefined
                        : () => {
                            if (longPressTimerRef.current) {
                              clearTimeout(longPressTimerRef.current);
                              longPressTimerRef.current = null;
                            }
                          }
                    }
                  >
                    {myPos && (
                      <>
                        <Circle
                          center={myPos}
                          radius={30}
                          options={{
                            fillColor: MY_LOCATION_BLUE,
                            fillOpacity: 0.15,
                            strokeOpacity: 0,
                            clickable: false,
                            zIndex: 1,
                          }}
                        />
                        <Circle
                          center={myPos}
                          radius={6}
                          options={{
                            fillColor: MY_LOCATION_BLUE,
                            fillOpacity: 1,
                            strokeColor: "#ffffff",
                            strokeOpacity: 1,
                            strokeWeight: 2,
                            clickable: false,
                            zIndex: 2,
                          }}
                        />
                      </>
                    )}
                  </GoogleMap>
                  {myPos && (
                    <button
                      type="button"
                      aria-label={t("MY_LOCATION")}
                      data-testid="recenter-button"
                      onClick={handleRecenter}
                      style={{
                        position: "absolute",
                        bottom: 80,
                        right: 10,
                        width: 44,
                        height: 44,
                        borderRadius: 22,
                        border: "none",
                        background: "#ffffff",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: 0,
                      }}
                    >
                      <IconSymbol
                        name="location.fill"
                        size={22}
                        color={MY_LOCATION_BLUE}
                      />
                    </button>
                  )}
                </>
              ) : (
                <LoadingIndicator text={t("LOADING_MAP")} />
              )}
              <Button
                text={t("CLOSE_MAP")}
                style={{
                  position: "absolute",
                  bottom: 20,
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: "calc(100% - 40px)" as any,
                }}
                onPress={() => setMapModalVisible(false)}
              />
            </div>
          )}
          {!mapModalVisible && (
            <div style={{ display: "flex", justifyContent: "center" }}>
              <Button
                testID="submit-post-button"
                text={isEditMode ? t("SAVE") : t("SUBMIT_POST")}
                onPress={confirmAndPost}
                disabled={!description}
              />
            </div>
          )}
        </div>
      </div>

      <CustomModal
        visible={alertModal.visible}
        onClose={() => setAlertModal({ ...alertModal, visible: false })}
        title={alertModal.title}
      >
        <ThemedText
          testID="alert-modal-message"
          style={{ marginBottom: 20, textAlign: "center" }}
        >
          {alertModal.message}
        </ThemedText>
        <div style={{ display: "flex", justifyContent: "center" }}>
          <Button
            testID="alert-modal-ok"
            text={t("OK")}
            onPress={() => setAlertModal({ ...alertModal, visible: false })}
            style={{ width: "auto", minWidth: 100 }}
          />
        </div>
      </CustomModal>

      <CustomModal
        visible={confirmModal.visible}
        onClose={() => setConfirmModal({ ...confirmModal, visible: false })}
        title={confirmModal.title}
      >
        <ThemedText style={{ marginBottom: 20, textAlign: "center" }}>
          {confirmModal.message}
        </ThemedText>
        <div style={{ display: "flex", gap: 16, justifyContent: "center" }}>
          <Button
            testID="confirm-post-cancel"
            text={t("CANCEL")}
            onPress={() => setConfirmModal({ ...confirmModal, visible: false })}
            style={{ backgroundColor: "gray", flex: 1 }}
          />
          <Button
            testID="confirm-post-yes"
            text={t("YES")}
            onPress={() => {
              setConfirmModal({ ...confirmModal, visible: false });
              confirmModal.onConfirm();
            }}
            style={{ flex: 1 }}
          />
        </div>
      </CustomModal>
    </ThemedView>
  );
}
